/* eslint-disable import/no-anonymous-default-export */
// Firebase Index Management Utility
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

// Collection names
const COLLECTIONS = {
  QUESTION_BANK: 'questionBank'
};

// Index status checker
export const checkIndexStatus = async () => {
  const results = {
    questionBank: {
      exam_createdAt: false,
      type_createdAt: false,
      difficulty_createdAt: false,
      topic_createdAt: false,
      status_createdAt: false
    }
  };

  try {
    // Test each index combination
    const testQueries = [
      { name: 'exam_createdAt', query: query(collection(db, COLLECTIONS.QUESTION_BANK), where('exam', '==', 'test'), orderBy('createdAt', 'desc')) },
      { name: 'type_createdAt', query: query(collection(db, COLLECTIONS.QUESTION_BANK), where('type', '==', 'test'), orderBy('createdAt', 'desc')) },
      { name: 'difficulty_createdAt', query: query(collection(db, COLLECTIONS.QUESTION_BANK), where('difficulty', '==', 'test'), orderBy('createdAt', 'desc')) },
      { name: 'topic_createdAt', query: query(collection(db, COLLECTIONS.QUESTION_BANK), where('topic', '==', 'test'), orderBy('createdAt', 'desc')) },
      { name: 'status_createdAt', query: query(collection(db, COLLECTIONS.QUESTION_BANK), where('status', '==', 'test'), orderBy('createdAt', 'desc')) }
    ];

    for (const testQuery of testQueries) {
      try {
        await getDocs(testQuery.query);
        results.questionBank[testQuery.name] = true;
      } catch (error) {
        if (error.code === 'failed-precondition' || error.message.includes('index')) {
          results.questionBank[testQuery.name] = false;
        } else {
          // Other error, assume index is working
          results.questionBank[testQuery.name] = true;
        }
      }
    }

    return results;
  } catch (error) {
    return results;
  }
};

// Get index building status
export const getIndexBuildingStatus = async () => {
  try {
    const status = await checkIndexStatus();
    const allBuilt = Object.values(status.questionBank).every(built => built);
    
    return {
      allBuilt,
      details: status,
      message: allBuilt 
        ? 'All indexes are built and ready' 
        : 'Some indexes are still building. This may take a few minutes.'
    };
  } catch (error) {
    return {
      allBuilt: false,
      details: null,
      message: 'Unable to check index status'
    };
  }
};

// Wait for indexes to be ready
export const waitForIndexes = async (maxWaitTime = 300000) => { // 5 minutes
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitTime) {
    const status = await getIndexBuildingStatus();
    
    if (status.allBuilt) {
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
  }
  
  return false;
};

export const handleIndexError = (error, context = 'query') => {
  if (error.code === 'failed-precondition' || error.message.includes('index')) {
    // Extract collection and fields from error message
    const errorMessage = error.message || '';
    const collectionMatch = errorMessage.match(/collection\s+([^\s]+)/i);
    const fieldMatch = errorMessage.match(/fields\s+\[([^\]]+)\]/i);
    
    const collection = collectionMatch ? collectionMatch[1] : 'unknown';
    const fields = fieldMatch ? fieldMatch[1].split(',').map(f => f.trim()) : [];
    
    // Generate Firebase Console URL for creating the index
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
    const createIndexUrl = `https://console.firebase.google.com/project/${projectId}/firestore/indexes`;
    
    return {
      type: 'index_required',
      context,
      collection,
      fields,
      createIndexUrl,
      error: error
    };
  }
  
  return {
    type: 'other_error',
    context,
    error: error
  };
};

// Check if an error is an index error
export const isIndexError = (error) => {
  return error.code === 'failed-precondition' || error.message.includes('index');
};

// Enhanced query with automatic fallback
export const queryWithFallback = async (collectionName, filters = {}, options = {}) => {
  const { useFallback = true, maxRetries = 3 } = options;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      let q = collection(db, collectionName);
      const conditions = [];
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          conditions.push(where(key, '==', value));
        }
      });
      
      // Add ordering
      if (options.orderBy) {
        conditions.push(orderBy(options.orderBy.field, options.orderBy.direction || 'desc'));
      }
      
      // Add limit
      if (options.limit) {
        conditions.push(limit(options.limit));
      }
      
      // Create and execute query
      if (conditions.length > 0) {
        q = query(q, ...conditions);
      }
      
      const snapshot = await getDocs(q);
      const results = [];
      snapshot.forEach(doc => {
        results.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, data: results };
      
    } catch (error) {
      // If it's an index error and we should use fallback
      if ((error.code === 'failed-precondition' || error.message.includes('index')) && useFallback) {
        try {
          // Try without ordering
          let fallbackQuery = collection(db, collectionName);
          const fallbackConditions = [];
          
          // Add filters but skip ordering
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              fallbackConditions.push(where(key, '==', value));
            }
          });
          
          // Add limit
          if (options.limit) {
            fallbackConditions.push(limit(options.limit));
          }
          
          if (fallbackConditions.length > 0) {
            fallbackQuery = query(fallbackQuery, ...fallbackConditions);
          }
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          const fallbackResults = [];
          fallbackSnapshot.forEach(doc => {
            fallbackResults.push({ id: doc.id, ...doc.data() });
          });
          
          // Sort in memory if we have results and ordering was requested
          if (fallbackResults.length > 0 && options.orderBy) {
            fallbackResults.sort((a, b) => {
              const aValue = a[options.orderBy.field];
              const bValue = b[options.orderBy.field];
              
              if (options.orderBy.direction === 'desc') {
                return bValue - aValue;
              }
              return aValue - bValue;
            });
          }
          
          return { success: true, data: fallbackResults, usedFallback: true };
          
        } catch (fallbackError) {
          }
      }
      
      // If this is the last attempt, return error
      if (attempt === maxRetries) {
        return { success: false, error };
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
    }
  }
};
